/**
 * QR Menu panel — Ρυθμίσεις › QR Menu (Ιούν 2026)
 *
 * Δημιουργία/ανανέωση του δημόσιου link+QR, λήψη QR ως PDF/JPG,
 * toggle εμφάνισης σχολίων. Τα είδη μαρκάρονται στο tab Είδη
 * (switch «QR MENU» ανά είδος).
 */
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import { QRMENU_FONTS, loadQrMenuFonts, lineStyle } from "@/lib/qrmenuFonts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { QrCode, RefreshCw, Copy, FileDown, ImageDown, ExternalLink, Bold, Italic, Save, ImagePlus, Trash2 } from "lucide-react";

export default function QRMenuPanel({ settings, onSettingsChange }) {
  const token = settings?.qrmenu_token || "";
  const url = token ? `${window.location.origin}/menu/${token}` : "";
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState(null);

  useEffect(() => { loadQrMenuFonts(); }, []);
  useEffect(() => {
    if (settings && !lines) {
      setLines({
        line1: { text: settings.qrmenu_line1 || "", font: settings.qrmenu_line1_font || "GFS Didot", bold: settings.qrmenu_line1_bold !== false, italic: !!settings.qrmenu_line1_italic },
        line2: { text: settings.qrmenu_line2 || "", font: settings.qrmenu_line2_font || "Open Sans", bold: !!settings.qrmenu_line2_bold, italic: !!settings.qrmenu_line2_italic },
      });
    }
  }, [settings, lines]);

  const saveLines = async () => {
    const merged = {
      ...settings,
      qrmenu_line1: lines.line1.text,
      qrmenu_line1_font: lines.line1.font,
      qrmenu_line1_bold: lines.line1.bold,
      qrmenu_line1_italic: lines.line1.italic,
      qrmenu_line2: lines.line2.text,
      qrmenu_line2_font: lines.line2.font,
      qrmenu_line2_bold: lines.line2.bold,
      qrmenu_line2_italic: lines.line2.italic,
    };
    try {
      await http.put("/settings", merged);
      onSettingsChange(merged);
      toast.success("Αποθηκεύτηκε το όνομα μαγαζιού");
    } catch {
      toast.error("Αποτυχία αποθήκευσης");
    }
  };

  useEffect(() => {
    if (!url) { setQrDataUrl(""); return; }
    QRCode.toDataURL(url, { width: 640, margin: 2 }).then(setQrDataUrl).catch(() => {});
  }, [url]);

  const regenerate = async () => {
    let admin_code = "";
    if (token) {
      if (!(await confirmDialog("Ανανέωση QR; Τα ήδη τυπωμένα QR θα σταματήσουν να λειτουργούν."))) return;
      admin_code = await promptDialog("Κωδικός διαχειριστή για επιβεβαίωση:");
      if (!admin_code) return;
    }
    setBusy(true);
    try {
      const r = await http.post("/qrmenu/regenerate", { admin_code });
      onSettingsChange({ ...settings, qrmenu_token: r.data.token });
      toast.success("Δημιουργήθηκε νέο QR link");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const toggleComments = async (v) => {
    const merged = { ...settings, qrmenu_show_comments: v };
    onSettingsChange(merged);
    try {
      await http.put("/settings", merged);
      toast.success("Αποθηκεύτηκε");
    } catch {
      toast.error("Αποτυχία αποθήκευσης");
    }
  };

  const saveLogo = async (dataUrl) => {
    const merged = { ...settings, qrmenu_logo: dataUrl };
    try {
      await http.put("/settings", merged);
      onSettingsChange(merged);
      toast.success(dataUrl ? "Αποθηκεύτηκε το λογότυπο" : "Αφαιρέθηκε το λογότυπο");
    } catch {
      toast.error("Αποτυχία αποθήκευσης λογοτύπου");
    }
  };

  const onLogoFile = (file) => {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      // Σμίκρυνση σε max 512px για μικρό μέγεθος στο settings doc
      const max = 512;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      saveLogo(canvas.toDataURL("image/png"));
    };
    img.onerror = () => toast.error("Μη έγκυρη εικόνα");
    img.src = URL.createObjectURL(file);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Αντιγράφηκε το link");
    } catch {
      toast.error("Αποτυχία αντιγραφής");
    }
  };

  // Σύνθεση poster σε canvas: λογότυπο + 2 γραμμές ονόματος (ή fallback) + QR + link
  const buildPosterCanvas = async () => {
    const qr = await QRCode.toDataURL(url, { width: 900, margin: 1 });
    const l1 = lines?.line1?.text ? lines.line1 : null;
    const l2 = lines?.line2?.text ? lines.line2 : null;
    const logoUrl = settings?.qrmenu_logo || "";
    const fontsToLoad = [l1, l2].filter(Boolean).map(
      (l) => `${l.italic ? "italic " : ""}${l.bold ? "700" : "400"} 52px "${l.font}"`,
    );
    try { await Promise.all(fontsToLoad.map((f) => document.fonts.load(f))); } catch { /* noop */ }
    const canvas = document.createElement("canvas");
    canvas.width = 1000;
    canvas.height = logoUrl ? 1480 : 1250;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.textAlign = "center";
    let y = 90;
    if (logoUrl) {
      const logo = new Image();
      await new Promise((res) => { logo.onload = res; logo.onerror = res; logo.src = logoUrl; });
      if (logo.width) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(500, 145, 105, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(logo, 395, 40, 210, 210);
        ctx.restore();
      }
      y = 320;
    }
    if (l1 || l2) {
      if (l1) {
        ctx.fillStyle = "#111111";
        ctx.font = `${l1.italic ? "italic " : ""}${l1.bold ? "700" : "400"} 56px "${l1.font}", serif`;
        ctx.fillText(l1.text, 500, y);
        y += 55;
      }
      if (l2) {
        ctx.fillStyle = "#444444";
        ctx.font = `${l2.italic ? "italic " : ""}${l2.bold ? "700" : "400"} 38px "${l2.font}", sans-serif`;
        ctx.fillText(l2.text, 500, y);
        y += 55;
      }
    } else {
      ctx.fillStyle = "#111111";
      ctx.font = "bold 52px sans-serif";
      ctx.fillText(settings?.company_name || (settings?.business_lines || [])[0] || "Τιμοκατάλογος", 500, y);
      y += 55;
    }
    ctx.font = "30px sans-serif";
    ctx.fillStyle = "#555555";
    ctx.fillText("Σκανάρετε για τον τιμοκατάλογο", 500, y + 40);
    const img = new Image();
    await new Promise((res) => { img.onload = res; img.src = qr; });
    ctx.drawImage(img, 75, y + 75, 850, 850);
    ctx.font = "26px monospace";
    ctx.fillStyle = "#888888";
    ctx.fillText(url, 500, y + 75 + 850 + 60);
    return canvas;
  };

  const downloadJpg = async () => {
    try {
      const canvas = await buildPosterCanvas();
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/jpeg", 0.92);
      a.download = "qr-menu.jpg";
      a.click();
    } catch {
      toast.error("Αποτυχία δημιουργίας JPG");
    }
  };

  const downloadPdf = async () => {
    try {
      const canvas = await buildPosterCanvas();
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const w = 170;
      const h = (canvas.height / canvas.width) * w;
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 20, 20, w, h);
      pdf.save("qr-menu.pdf");
    } catch {
      toast.error("Αποτυχία δημιουργίας PDF");
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="qrmenu-panel">
      <div>
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
          <QrCode size={18} /> QR Menu — Δημόσιος Τιμοκατάλογος
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Ο πελάτης σκανάρει το QR και βλέπει μόνο τον τιμοκατάλογο (κατηγορίες, είδη, τιμές) στο κινητό του —
          καμία πρόσβαση στο πρόγραμμα. Μαρκάρετε τα είδη που θα εμφανίζονται από το tab «Είδη» (διακόπτης «QR MENU»).
          Είδη σε «Παύση Πώλησης» κρύβονται αυτόματα.
        </p>
      </div>

      {!token ? (
        <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center space-y-3">
          <div className="text-sm text-slate-500">Δεν έχει δημιουργηθεί ακόμα QR link για αυτό το κατάστημα.</div>
          <Button onClick={regenerate} disabled={busy} data-testid="qrmenu-create-btn">
            <QrCode size={16} className="mr-1.5" /> Δημιουργία QR
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-5 items-start border-2 border-slate-200 rounded-xl p-5">
            {qrDataUrl && (
              <img src={qrDataUrl} alt="QR Menu" className="w-44 h-44 border rounded-lg" data-testid="qrmenu-qr-img" />
            )}
            <div className="flex-1 space-y-3 min-w-0">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Δημόσιο link</div>
                <div className="font-mono text-sm break-all" data-testid="qrmenu-link">{url}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="qrmenu-copy-btn">
                  <Copy size={14} className="mr-1" /> Αντιγραφή
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")} data-testid="qrmenu-open-btn">
                  <ExternalLink size={14} className="mr-1" /> Προεπισκόπηση
                </Button>
                <Button size="sm" variant="outline" onClick={downloadJpg} data-testid="qrmenu-jpg-btn">
                  <ImageDown size={14} className="mr-1" /> Λήψη JPG
                </Button>
                <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="qrmenu-pdf-btn">
                  <FileDown size={14} className="mr-1" /> Λήψη PDF
                </Button>
              </div>
              <Button size="sm" variant="destructive" onClick={regenerate} disabled={busy} data-testid="qrmenu-regen-btn">
                <RefreshCw size={14} className="mr-1" /> Ανανέωση QR (αχρηστεύει τα παλιά)
              </Button>
            </div>
          </div>

          <label className="flex items-center gap-3 p-4 border-2 border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <Switch
              checked={settings?.qrmenu_show_comments !== false}
              onCheckedChange={toggleComments}
              data-testid="qrmenu-show-comments-toggle"
            />
            <div>
              <div className="font-bold text-sm">Εμφάνιση σχολίων ειδών</div>
              <div className="text-[11px] text-slate-500">
                Εμφανίζει το «Σχόλιο QR Menu» κάθε είδους κάτω από το όνομά του
              </div>
            </div>
          </label>

          {/* Λογότυπο τιμοκαταλόγου */}
          <div className="border-2 border-slate-200 rounded-xl p-5 space-y-3" data-testid="qrmenu-logo-card">
            <div>
              <div className="font-bold text-sm">Λογότυπο (τιμοκατάλογος & poster)</div>
              <div className="text-[11px] text-slate-500">
                Έγχρωμη εικόνα PNG/JPG — εμφανίζεται πάνω από το όνομα. Σμικρύνεται αυτόματα.
              </div>
            </div>
            <div className="flex items-center gap-4">
              {settings?.qrmenu_logo ? (
                <img
                  src={settings.qrmenu_logo}
                  alt="logo"
                  className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                  data-testid="qrmenu-logo-preview"
                />
              ) : (
                <div className="w-20 h-20 rounded-full border-2 border-dashed border-slate-300 grid place-items-center text-slate-400">
                  <ImagePlus size={22} />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => { onLogoFile(e.target.files?.[0]); e.target.value = ""; }}
                    data-testid="qrmenu-logo-input"
                  />
                  <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-bold hover:bg-slate-700">
                    <ImagePlus size={14} /> {settings?.qrmenu_logo ? "Αλλαγή λογοτύπου" : "Ανέβασμα λογοτύπου"}
                  </span>
                </label>
                {settings?.qrmenu_logo && (
                  <Button size="sm" variant="outline" onClick={() => saveLogo("")} data-testid="qrmenu-logo-remove">
                    <Trash2 size={14} className="mr-1" /> Αφαίρεση
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Όνομα μαγαζιού — 2 γραμμές με γραμματοσειρά, bold, italic */}
          {lines && (
            <div className="border-2 border-slate-200 rounded-xl p-5 space-y-4" data-testid="qrmenu-lines-card">
              <div>
                <div className="font-bold text-sm">Όνομα μαγαζιού στο QR Menu (2 γραμμές)</div>
                <div className="text-[11px] text-slate-500">
                  Εμφανίζονται στην κορυφή του τιμοκαταλόγου και στο poster (JPG/PDF). Άφησέ τες κενές για το προεπιλεγμένο όνομα.
                </div>
              </div>
              {["line1", "line2"].map((key, idx) => {
                const l = lines[key];
                const set = (patch) => setLines({ ...lines, [key]: { ...l, ...patch } });
                return (
                  <div key={key} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 w-14">Γραμμή {idx + 1}</span>
                    <Input
                      value={l.text}
                      onChange={(e) => set({ text: e.target.value })}
                      placeholder={idx === 0 ? "π.χ. Καφέ Ονειρο" : "π.χ. Coffee · Snacks · Drinks"}
                      className="flex-1 min-w-[180px]"
                      style={lineStyle(l)}
                      data-testid={`qrmenu-${key}-text`}
                    />
                    <select
                      value={l.font}
                      onChange={(e) => set({ font: e.target.value })}
                      className="h-9 border-2 border-slate-200 rounded-md px-2 text-sm bg-white"
                      style={{ fontFamily: `'${l.font}', serif` }}
                      data-testid={`qrmenu-${key}-font`}
                    >
                      {QRMENU_FONTS.map((f) => (
                        <option key={f} value={f} style={{ fontFamily: `'${f}', serif` }}>{f}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant={l.bold ? "default" : "outline"}
                      className="w-9 px-0"
                      onClick={() => set({ bold: !l.bold })}
                      data-testid={`qrmenu-${key}-bold`}
                    >
                      <Bold size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant={l.italic ? "default" : "outline"}
                      className="w-9 px-0"
                      onClick={() => set({ italic: !l.italic })}
                      data-testid={`qrmenu-${key}-italic`}
                    >
                      <Italic size={14} />
                    </Button>
                  </div>
                );
              })}
              {/* Προεπισκόπηση header */}
              {(lines.line1.text || lines.line2.text) && (
                <div className="rounded-lg bg-zinc-950 py-5 px-4 text-center" data-testid="qrmenu-lines-preview">
                  {lines.line1.text && (
                    <div className="text-2xl text-amber-50" style={lineStyle(lines.line1)}>{lines.line1.text}</div>
                  )}
                  {lines.line2.text && (
                    <div className="text-base text-amber-100/90 mt-1" style={lineStyle(lines.line2)}>{lines.line2.text}</div>
                  )}
                </div>
              )}
              <Button size="sm" onClick={saveLines} data-testid="qrmenu-lines-save">
                <Save size={14} className="mr-1" /> Αποθήκευση ονόματος
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
